import axios from 'axios';

const getCsrfToken = () => {
  const name = 'csrftoken';
  const cookieValue = document.cookie.split('; ').find(row => row.startsWith(name))?.split('=')[1];
  return cookieValue;
};

const CLIENT_TOKEN = process.env.REACT_APP_CLIENT_TOKEN; // Token sincronizado con el backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Base URL para las APIs

// ---------------------------------------------------------------------------
// Cliente HTTP con interceptores (Bearer + CSRF + refresh automático en 401)
// ---------------------------------------------------------------------------
// Toda llamada autenticada debe pasar por `apiClient` para heredar:
//  1. Authorization: Bearer <access> (desde localStorage)
//  2. X-CSRFToken leído de la cookie csrftoken
//  3. Reintento único ante 401: refresca el access con el refresh y reintenta.

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // manda cookies de sesión y CSRF
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const access = localStorage.getItem('access');
  if (access) config.headers['Authorization'] = `Bearer ${access}`;
  const csrf = getCsrfToken();
  if (csrf) config.headers['X-CSRFToken'] = csrf;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true; // reintento único: evita el bucle 401 -> refresh -> 401
      const refresh = localStorage.getItem('refresh');
      if (refresh) {
        try {
          // axios crudo (NO apiClient) para no reentrar en este interceptor
          const { data } = await axios.post(
            `${API_BASE_URL}/users/token/refresh`,
            { refresh },
            { headers: { 'X-CSRFToken': getCsrfToken() }, withCredentials: true }
          );
          localStorage.setItem('access', data.access);
          if (data.refresh) localStorage.setItem('refresh', data.refresh);
          original.headers['Authorization'] = `Bearer ${data.access}`;
          return apiClient(original);
        } catch (refreshError) {
          // El refresh también caducó: limpiar para forzar nuevo login
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Siembra la cookie CSRF. Llamar antes de cada POST sin Bearer (login/registro).
export const ensureCsrfToken = async () => {
  await apiClient.get('/users/csrf');
};

// Funciones para el usuario

export const registerUser = async (payload) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/register', payload);
  // El registro ya emite JWT (y crea sesión Django): persistir ambos tokens
  // para que el interceptor de refresh funcione sin un segundo login.
  if (response.data.access) localStorage.setItem('access', response.data.access);
  if (response.data.refresh) localStorage.setItem('refresh', response.data.refresh);
  return response.data;
};

export const loginUser = async (document, password) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/login', { document, password });
  // Persistir ambos tokens: el refresh es indispensable para el interceptor.
  if (response.data.access) localStorage.setItem('access', response.data.access);
  if (response.data.refresh) localStorage.setItem('refresh', response.data.refresh);
  return response.data;
};

export const logoutUser = async (refreshToken, accessToken) => {
  await apiClient.post('/users/logout', { refresh: refreshToken }, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
};

export const getUserDetails = async (token) => {
  const response = await apiClient.get('/users/info', {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return response.data;
};

export const getUserProfile = async (token) => {
  const response = await apiClient.get('/users/profile', {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return response.data;
};

export const updateUserProfile = async (token, formData) => {
  const response = await apiClient.patch('/users/profile', formData, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return response.data;
};

export const sendPasswordResetEmail = async (email) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/reset-password', { email });
  return response.data;
};

export const confirmPasswordReset = async (uidb64, token, newPassword) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/reset-password/confirm', {
    uidb64,
    token,
    new_password: newPassword,
  });
  return response.data;
};

export const changePassword = async (token, currentPassword, newPassword) => {
  const response = await apiClient.post('/users/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  }, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return response.data;
};

// Funciones para los formularios

export const fetchForms = async () => {
  const response = await fetch(`${API_BASE_URL}/forms/forms`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-Client-Token': CLIENT_TOKEN,
    },
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

export const checkDocument = async (documentNumber) => {
  const response = await fetch(`${API_BASE_URL}/forms/completed-forms/check/${documentNumber}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-Client-Token': CLIENT_TOKEN,
    },
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

export const submitForm = async (formTitle, userName, email, dataToSubmit, token) => {
  const completedFormData = {
    form_title: formTitle,
    user: userName,
    email: email,
    content: dataToSubmit,
  };

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRFToken': getCsrfToken(),
    'X-Client-Token': CLIENT_TOKEN,
  };

  // Agregar el token de autorización si está disponible
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/forms/completed-forms`, {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify(completedFormData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Error al enviar el formulario');
  }
  return response.json();
};

export const fetchCategoryAverages = async (documentNumber) => {
  const response = await fetch(`${API_BASE_URL}/forms/category-averages/${documentNumber}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-Client-Token': CLIENT_TOKEN,
    },
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

// Nuevas funciones para el mentor

export const getMentorAvailability = async (token, mentorId) => {
  const response = await axios.get(`${API_BASE_URL}/mentoring/mentor-availability?mentor=${mentorId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const createAvailability = async (token, availabilityData) => {
  const response = await axios.post(`${API_BASE_URL}/mentoring/availability`, availabilityData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const deleteAvailability = async (token, availabilityId) => {
  const response = await axios.delete(`${API_BASE_URL}/mentoring/availability/${availabilityId}`, {
      headers: {
          'Authorization': `Bearer ${token}`,
          'X-CSRFToken': getCsrfToken(),
      },
      withCredentials: true,
  });
  return response.data;
};

export const getMentorSessions = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/mentoring/mentor-sessions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const updateSessionStatus = async (token, sessionId, action) => {
  const response = await axios.patch(`${API_BASE_URL}/mentoring/update-session-status/${sessionId}`, { action }, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const getApprenticeSessions = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/mentoring/apprentice-sessions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const getAvailableMentors = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/mentoring/available-mentors`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const bookSession = async (token, availabilityId, subject, description) => {
  const response = await axios.post(`${API_BASE_URL}/mentoring/book-session`, {
    availability: availabilityId,
    subject,
    description
  }, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const updateMentorAbout = async (token, mentorId, about) => {
  const response = await axios.patch(`${API_BASE_URL}/mentoring/update-mentor-about/${mentorId}`, 
    { about }, 
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-CSRFToken': getCsrfToken(),
      },
      withCredentials: true,
    }
  );
  return response.data;
};

export const updateMentorCategories = async (token, mentorId, categories) => {
  const response = await axios.patch(`${API_BASE_URL}/mentoring/update-mentor-categories/${mentorId}`, 
    { categories }, 
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-CSRFToken': getCsrfToken(),
      },
      withCredentials: true,
    }
  );
  return response.data;
};

export const fetchCategories = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/mentoring/categories`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const rateMentor = async (token, mentorId, rating) => {
  const response = await fetch(`${API_BASE_URL}/mentoring/rate_mentor/${mentorId}`, {  // Asegúrate que mentorId no sea undefined
      method: 'PATCH',
      headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRFToken': getCsrfToken(),
      },
      body: JSON.stringify({ rating }),
  });

  if (!response.ok) {
      throw new Error('Error al calificar al mentor');
  }
};

// Instancia compartida: las nuevas llamadas de negocio deben usar `apiClient`
// para heredar Bearer + CSRF + refresh automático en 401.
export default apiClient;